import React, { useCallback, useEffect, useMemo, useState } from "react";
import useStyles from "./style";
import {
  Box,
  Button,
  Chip,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import PendingOutlinedIcon from "@mui/icons-material/PendingOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { showLoader, hideLoader } from "Redux/Loader/actions";
import { showSnackbar } from "Redux/SnackBar/actions";
import { useDispatch } from "react-redux";

import CorporateApi from "Services/Corporate";
import LewooPayApi from "Services/LewooPay";
import { apiUrl } from "../../../../../Constants";

const DEFAULT_ACCOUNT_STATUS = "ONBOARDING";
const DEFAULT_ACCOUNT_STATUSES = ["ONBOARDING", "APPROVED", "BANNED"];
const ACCOUNT_STATUS_LABELS = {
  ONBOARDING: "On Boarding",
  APPROVED: "Approve",
  BANNED: "Banned",
};
const APPROVED_STATUSES = new Set(["approved", "validated", "valid", "accepted"]);
const PENDING_STATUSES = new Set([
  "pending",
  "accessing",
  "in_review",
  "verification_pending",
  "review",
  "processing",
]);
const REJECTED_STATUSES = new Set(["rejected", "declined", "refused", "denied"]);
const EXPIRED_STATUSES = new Set(["expired"]);
const MISSING_STATUSES = new Set([
  "",
  "no_document",
  "not_uploaded",
  "missing",
  "missing_document",
  "not_available",
]);

const normalizeKey = (value) =>
  (value ?? "")
    .toString()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const normalizeUserType = (value) => {
  const normalized = (value ?? "").toString().trim().toUpperCase();
  return normalized === "DRIVER" ? "DRIVER" : "USER";
};

const normalizeAllowedAccountStatuses = (values = DEFAULT_ACCOUNT_STATUSES) => {
  const normalized = new Set(
    (Array.isArray(values) && values.length > 0 ? values : DEFAULT_ACCOUNT_STATUSES)
      .map((value) => (value ?? "").toString().trim().toUpperCase())
      .filter(Boolean)
  );

  return normalized.size > 0 ? normalized : new Set(DEFAULT_ACCOUNT_STATUSES);
};

const normalizeAccountStatus = (value, allowedAccountStatuses) => {
  const normalized = (value ?? "").toString().trim().toUpperCase();

  if (
    normalized === "APPROVED" ||
    normalized === "APPROVE" ||
    normalized === "VALIDATED" ||
    normalized === "VALID" ||
    normalized === "ACCEPTED"
  ) {
    return allowedAccountStatuses.has("APPROVED") ? "APPROVED" : "ONBOARDING";
  }

  if (normalized === "BANNED") {
    return allowedAccountStatuses.has("BANNED") ? "BANNED" : "ONBOARDING";
  }

  return "ONBOARDING";
};

const resolveMediaUrl = (value) => {
  if (!value) {
    return "";
  }

  const stringValue = value.toString();
  if (
    stringValue.startsWith("http://") ||
    stringValue.startsWith("https://") ||
    stringValue.startsWith("data:") ||
    stringValue.startsWith("blob:")
  ) {
    return stringValue;
  }

  return `${apiUrl}${stringValue}`;
};

const getDocumentMeta = (row) => {
  const rawStatus = row?.rawStatus || row?.status || "";
  const statusKey = normalizeKey(rawStatus);
  const hasFile = Boolean(row?.file);

  if (!hasFile || MISSING_STATUSES.has(statusKey)) {
    return {
      state: "missing",
      label: "Non charge",
      tone: "error",
      detail: "Aucun document telecharge",
      actionLabel: "Charger",
      actionType: "upload",
      actionIcon: <UploadFileOutlinedIcon fontSize="small" />,
    };
  }

  if (APPROVED_STATUSES.has(statusKey)) {
    return {
      state: "approved",
      label: "Valide",
      tone: "success",
      detail: "Document valide",
      actionLabel: "Voir",
      actionType: "view",
      actionIcon: <VisibilityOutlinedIcon fontSize="small" />,
    };
  }

  if (PENDING_STATUSES.has(statusKey)) {
    return {
      state: "pending",
      label: "En attente",
      tone: "warning",
      detail: "En cours de validation par l'administrateur",
      actionLabel: "Voir",
      actionType: "view",
      actionIcon: <VisibilityOutlinedIcon fontSize="small" />,
    };
  }

  if (EXPIRED_STATUSES.has(statusKey)) {
    return {
      state: "rejected",
      label: "Expire",
      tone: "error",
      detail: "Le document est expire",
      actionLabel: "Voir",
      actionType: "view",
      actionIcon: <VisibilityOutlinedIcon fontSize="small" />,
    };
  }

  if (REJECTED_STATUSES.has(statusKey)) {
    return {
      state: "rejected",
      label: "Rejete",
      tone: "error",
      detail: "Le document a ete rejete",
      actionLabel: "Voir",
      actionType: "view",
      actionIcon: <VisibilityOutlinedIcon fontSize="small" />,
    };
  }

  return {
    state: "pending",
    label: hasFile ? "En attente" : "Non charge",
    tone: hasFile ? "warning" : "error",
    detail: hasFile
      ? "En cours de validation par l'administrateur"
      : "Aucun document telecharge",
    actionLabel: hasFile ? "Voir" : "Charger",
    actionType: hasFile ? "view" : "upload",
    actionIcon: hasFile ? (
      <VisibilityOutlinedIcon fontSize="small" />
    ) : (
      <UploadFileOutlinedIcon fontSize="small" />
    ),
  };
};

const Status = ({
  passengerId,
  details,
  passengerDocuments = [],
  openDocumentsTab,
  userType = "USER",
  allowedAccountStatuses = DEFAULT_ACCOUNT_STATUSES,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const resolvedUserType = normalizeUserType(userType);
  const allowedAccountStatusSet = useMemo(
    () => normalizeAllowedAccountStatuses(allowedAccountStatuses),
    [allowedAccountStatuses]
  );
  const accountStatusOptions = useMemo(
    () =>
      DEFAULT_ACCOUNT_STATUSES.filter((status) => allowedAccountStatusSet.has(status)).map(
        (status) => ({
          value: status,
          label: ACCOUNT_STATUS_LABELS[status],
        })
      ),
    [allowedAccountStatusSet]
  );

  const [isEditable, setIsEditable] = useState(false);
  const [passengerStatus, setPassengerStatus] = useState(undefined);
  const [accountStatus, setAccountStatus] = useState(undefined);
  const [isCreatingLewooPay, setIsCreatingLewooPay] = useState(false);
  const [lewooPayCreated, setLewooPayCreated] = useState(false);

  useEffect(() => {
    if (!details || Object.keys(details).length === 0) {
      setAccountStatus(undefined);
      setPassengerStatus(undefined);
      setIsEditable(false);
      setLewooPayCreated(false);
      return;
    }
    const rawAccountStatus =
      details?.customer_details?.accountStatus ?? details?.accountStatus ?? details?.status ?? "";
    const nextAccountStatus = normalizeAccountStatus(rawAccountStatus, allowedAccountStatusSet);

    setAccountStatus(nextAccountStatus);
    setPassengerStatus(
      nextAccountStatus === "BANNED"
        ? false
        : details?.passengerAccountStatus === "ACTIVE" ||
            (nextAccountStatus === "APPROVED" &&
              details?.passengerAccountStatus !== "DE_ACTIVE")
    );
    setLewooPayCreated(false);
  }, [allowedAccountStatusSet, details]);

  const updatePassengerService = useCallback(
    async (nextAccountStatus, nextPassengerStatus) => {
      if (!passengerId) {
        return false;
      }

      dispatch(showLoader("Loading please wait..."));

      try {
        const payload = {
          accountStatus: nextAccountStatus,
          passengerAccountStatus:
            nextAccountStatus === "BANNED"
              ? "DE_ACTIVE"
              : nextPassengerStatus
              ? "ACTIVE"
              : "DE_ACTIVE",
          userType: resolvedUserType,
        };

        const response = await CorporateApi.updateCorporate(passengerId, payload);

        if (response?.status === 200 || response?.status === 201) {
          dispatch(
            showSnackbar({
              message: "Corporate status updated successfully",
              autoHideDuration: 3000,
              anchorOrigin: {
                vertical: "top",
                horizontal: "right",
              },
              variant: "success",
            })
          );
          return true;
        }

        throw new Error("Unexpected status update response");
      } catch (error) {
        dispatch(
          showSnackbar({
            message:
              error?.response?.data?.message ||
              "Error while updating the passenger status",
            autoHideDuration: 3000,
            anchorOrigin: {
              vertical: "top",
              horizontal: "right",
            },
            variant: "error",
          })
        );
        return false;
      } finally {
        dispatch(hideLoader());
      }
    },
    [dispatch, passengerId, resolvedUserType]
  );

  const handleAccountStatus = async (event) => {
    if (!isEditable) {
      return;
    }

    const nextAccountStatus = event.target.value;
    if (!allowedAccountStatusSet.has(nextAccountStatus)) {
      return;
    }
    const previousAccountStatus = accountStatus;
    const previousPassengerStatus = passengerStatus;
    const nextPassengerStatus =
      nextAccountStatus === "BANNED" ? false : previousPassengerStatus;

    setAccountStatus(nextAccountStatus);
    setPassengerStatus(nextPassengerStatus);

    const success = await updatePassengerService(
      nextAccountStatus,
      nextPassengerStatus
    );

    if (!success) {
      setAccountStatus(previousAccountStatus);
      setPassengerStatus(previousPassengerStatus);
    }
  };

  const handleUpdateStates = async () => {
    if (!isEditable || accountStatus === "BANNED") {
      return;
    }

    const nextPassengerStatus = !passengerStatus;
    const previousPassengerStatus = passengerStatus;

    setPassengerStatus(nextPassengerStatus);

    const success = await updatePassengerService(accountStatus, nextPassengerStatus);
    if (!success) {
      setPassengerStatus(previousPassengerStatus);
    }
  };

  const handleApprovalToggle = useCallback(
    async (event) => {
      if (!isEditable || accountStatus === "BANNED") {
        return;
      }

      const nextAccountStatus = event.target.checked ? "APPROVED" : DEFAULT_ACCOUNT_STATUS;
      if (!allowedAccountStatusSet.has(nextAccountStatus)) {
        return;
      }
      if (nextAccountStatus === accountStatus) {
        return;
      }

      const previousAccountStatus = accountStatus;
      const previousPassengerStatus = passengerStatus;

      setAccountStatus(nextAccountStatus);

      const success = await updatePassengerService(
        nextAccountStatus,
        previousPassengerStatus
      );

      if (!success) {
        setAccountStatus(previousAccountStatus);
        setPassengerStatus(previousPassengerStatus);
      }
    },
    [accountStatus, allowedAccountStatusSet, isEditable, passengerStatus, updatePassengerService]
  );

  const handleOpenDocumentsTab = useCallback(() => {
    if (typeof openDocumentsTab === "function") {
      openDocumentsTab();
    }
  }, [openDocumentsTab]);

  const documentRows = useMemo(
    () =>
      (Array.isArray(passengerDocuments) ? passengerDocuments : []).map(
        (document, index) => {
          const file = resolveMediaUrl(document?.file);
          const rowData = {
            id: document?.documentId || document?.id || index + 1,
            documentId: document?.documentId || document?.id || index + 1,
            documentType: document?.documentType || `Document ${index + 1}`,
            documentTypeAlias:
              document?.documentTypeAlias ||
              normalizeKey(document?.documentType || `Document ${index + 1}`),
            file,
            fileName: document?.fileName || document?.documentType || "Document",
            rawStatus: document?.rawStatus || document?.status || "",
          };

          const statusMeta = getDocumentMeta(rowData);

          return {
            ...rowData,
            statusMeta,
            statusLabel: statusMeta.label,
            detail: statusMeta.detail,
            actionType: statusMeta.actionType,
          };
        }
      ),
    [passengerDocuments]
  );

  const documentStats = useMemo(() => {
    const totals = documentRows.reduce(
      (acc, row) => {
        if (row.statusMeta?.state === "approved") {
          acc.validated += 1;
        } else if (row.statusMeta?.state === "pending") {
          acc.pending += 1;
        } else if (row.statusMeta?.state === "rejected") {
          acc.rejected += 1;
        } else {
          acc.missing += 1;
        }

        return acc;
      },
      {
        total: documentRows.length,
        validated: 0,
        pending: 0,
        rejected: 0,
        missing: 0,
      }
    );

    const rejectedMissing = totals.rejected + totals.missing;
    const progress = totals.total > 0 ? Math.round((totals.validated / totals.total) * 100) : 0;

    return {
      ...totals,
      rejectedMissing,
      progress,
    };
  }, [documentRows]);

  const isFullyValidated =
    documentStats.total > 0 && documentStats.validated === documentStats.total;
  const documentAttentionCount = documentStats.pending + documentStats.rejectedMissing;
  const canCreateAccount =
    isFullyValidated &&
    accountStatus === "APPROVED" &&
    Boolean(passengerStatus) &&
    !isCreatingLewooPay &&
    !lewooPayCreated;
  const progressColor = isFullyValidated ? "#11823b" : "#dc2626";

  const handleCreateLewooPayAccount = useCallback(async () => {
    if (!details || !isFullyValidated || accountStatus !== "APPROVED" || !passengerStatus) {
      return;
    }

    dispatch(showLoader("Loading please wait..."));
    setIsCreatingLewooPay(true);

    try {
      const response = await LewooPayApi.createAccount(details, resolvedUserType);

      dispatch(
        showSnackbar({
          message: response?.data?.message || "LewooPay account created successfully",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "success",
        })
      );

      setLewooPayCreated(true);
    } catch (error) {
      dispatch(
        showSnackbar({
          message:
            error?.response?.data?.message ||
            error?.message ||
            "Error while creating LewooPay account",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        })
      );
    } finally {
      setIsCreatingLewooPay(false);
      dispatch(hideLoader());
    }
  }, [accountStatus, details, dispatch, isFullyValidated, passengerStatus, resolvedUserType]);

  const handleOpenDocument = useCallback((row) => {
    const url = resolveMediaUrl(row?.file);
    if (!url) {
      return;
    }

    window.open(url, "_blank", "noopener,noreferrer");
  }, []);

  const handleDocumentAction = useCallback(
    (row) => {
      if (row?.actionType === "upload") {
        handleOpenDocumentsTab();
        return;
      }

      handleOpenDocument(row);
    },
    [handleOpenDocument, handleOpenDocumentsTab]
  );

  const documentColumns = useMemo(
    () => [
      {
        field: "documentType",
        headerName: "Document",
        flex: 1.35,
        minWidth: 260,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          const row = params?.row || {};
          const showAlias =
            normalizeKey(row?.documentTypeAlias) !== normalizeKey(row?.documentType);

          return (
            <Box className={classes.documentCell}>
              <Box className={classes.documentIcon}>
                <DescriptionOutlinedIcon fontSize="small" />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" className={classes.documentName} noWrap>
                  {row?.documentType}
                </Typography>
                {showAlias && (
                  <Typography variant="caption" className={classes.documentAlias} noWrap>
                    {row?.documentTypeAlias}
                  </Typography>
                )}
              </Box>
            </Box>
          );
        },
      },
      {
        field: "status",
        headerName: "Status",
        flex: 0.8,
        minWidth: 160,
        sortable: false,
        disableColumnMenu: true,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => {
          const row = params?.row || {};
          const meta = row?.statusMeta || {};
          const toneColors = {
            success: { backgroundColor: "#e8f5e9", color: "#11823b" },
            warning: { backgroundColor: "#fff4e5", color: "#fb8c00" },
            error: { backgroundColor: "#fdecef", color: "#e53935" },
          };

          return (
            <Chip
              label={meta?.label || row?.statusLabel || "Pending"}
              size="small"
              sx={{
                height: 28,
                fontWeight: 800,
                borderRadius: 999,
                ...toneColors[meta?.tone || "warning"],
                "& .MuiChip-label": {
                  px: 1.5,
                },
              }}
            />
          );
        },
      },
      {
        field: "details",
        headerName: "Details",
        flex: 1.45,
        minWidth: 260,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <Typography variant="body2" className={classes.detailText}>
            {params?.row?.detail}
          </Typography>
        ),
      },
      {
        field: "action",
        headerName: "Actions",
        flex: 0.75,
        minWidth: 150,
        sortable: false,
        disableColumnMenu: true,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => {
          const row = params?.row || {};

          return (
            <Button
              variant="text"
              color="success"
              startIcon={row?.statusMeta?.actionIcon}
              onClick={() => handleDocumentAction(row)}
              className={classes.rowActionButton}
              size="small"
            >
              {row?.statusMeta?.actionLabel || "Voir"}
            </Button>
          );
        },
      },
    ],
    [classes, handleDocumentAction]
  );

  if (accountStatus === undefined || passengerStatus === undefined) {
    return (
      <Box className={classes.loadingState}>
        <Typography variant="body2">Loading status...</Typography>
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        className={classes.editRow}
      >
        <FormControlLabel
          control={
            <Switch
              checked={isEditable}
              onChange={(event) => setIsEditable(event.target.checked)}
              color="success"
            />
          }
          label={<Typography className={classes.toggleLabel}>Enable Edit</Typography>}
        />
      </Stack>

      <Typography variant="body2" className={classes.noteText}>
        Note: Manually updating the passenger status will reflect the corresponding
        changes in the app flow.
      </Typography>

      <Paper variant="outlined" className={classes.statusPanel}>
        <Box className={!isEditable ? classes.statusControlsLocked : ""}>
          <Box className={classes.statusHeaderRow}>
            <Typography variant="body1" className={classes.fieldLabel}>
              Account Status
            </Typography>
            <FormControl disabled={!isEditable}>
              <RadioGroup
                row
                value={accountStatus}
                onChange={handleAccountStatus}
                className={classes.radioGroup}
              >
                {accountStatusOptions.map((option) => (
                  <FormControlLabel
                    key={option.value}
                    value={option.value}
                    control={<Radio color="success" />}
                    label={option.label}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </Box>

          <Divider className={classes.sectionDivider} />

          <Box className={classes.statusToggleRow}>
            <Typography variant="body1" className={classes.fieldLabel}>
              Corporate Status (Active/De-active)
            </Typography>
            <Switch
              checked={Boolean(passengerStatus)}
              onChange={handleUpdateStates}
              color="success"
              disabled={!isEditable || accountStatus === "BANNED"}
              className={classes.statusSwitch}
            />
          </Box>
        </Box>
      </Paper>

      <Paper variant="outlined" className={classes.verificationPanel}>
        <Box className={classes.verificationHeader}>
          <Box>
            <Typography variant="h6" className={classes.verificationTitle}>
              VERIFICATION DES DOCUMENTS
            </Typography>
            <Typography variant="body2" className={classes.verificationSubtitle}>
              Verifiez si tous les documents requis ont ete charges et valides.
            </Typography>
          </Box>

          <Button
            variant="outlined"
            color="success"
            startIcon={<DescriptionOutlinedIcon />}
            onClick={handleOpenDocumentsTab}
            className={classes.verifyBtn}
          >
            Verifier les documents
          </Button>
        </Box>

        <Box className={classes.metricsGrid}>
          <Paper variant="outlined" className={classes.metricCard}>
            <Box className={classes.metricIcon} sx={{ backgroundColor: "#edf4ff", color: "#2563eb" }}>
              <DescriptionOutlinedIcon fontSize="small" />
            </Box>
            <Box className={classes.metricContent}>
              <Typography className={classes.metricLabel}>Documents requis</Typography>
              <Typography className={classes.metricValue}>{documentStats.total}</Typography>
              <Typography className={classes.metricHelper}>Pieces attendues</Typography>
            </Box>
          </Paper>

          <Paper variant="outlined" className={classes.metricCard}>
            <Box className={classes.metricIcon} sx={{ backgroundColor: "#e8f5e9", color: "#11823b" }}>
              <CheckCircleOutlineOutlinedIcon fontSize="small" />
            </Box>
            <Box className={classes.metricContent}>
              <Typography className={classes.metricLabel}>Valides</Typography>
              <Typography className={classes.metricValue}>{documentStats.validated}</Typography>
              <Typography className={classes.metricHelper}>Documents approuves</Typography>
            </Box>
          </Paper>

          <Paper variant="outlined" className={classes.metricCard}>
            <Box className={classes.metricIcon} sx={{ backgroundColor: "#fff4e5", color: "#fb8c00" }}>
              <PendingOutlinedIcon fontSize="small" />
            </Box>
            <Box className={classes.metricContent}>
              <Typography className={classes.metricLabel}>En attente</Typography>
              <Typography className={classes.metricValue}>{documentStats.pending}</Typography>
              <Typography className={classes.metricHelper}>En cours de revue</Typography>
            </Box>
          </Paper>

          <Paper variant="outlined" className={classes.metricCard}>
            <Box className={classes.metricIcon} sx={{ backgroundColor: "#fdecef", color: "#e53935" }}>
              <ErrorOutlineOutlinedIcon fontSize="small" />
            </Box>
            <Box className={classes.metricContent}>
              <Typography className={classes.metricLabel}>Rejetes / Manquants</Typography>
              <Typography className={classes.metricValue}>{documentStats.rejectedMissing}</Typography>
              <Typography className={classes.metricHelper}>
                {documentStats.rejected} rejetes, {documentStats.missing} manquants
              </Typography>
            </Box>
          </Paper>

          <Paper variant="outlined" className={classes.metricCard}>
            <Box className={classes.metricIcon} sx={{ backgroundColor: "#eef2ff", color: progressColor }}>
              <CheckCircleOutlineOutlinedIcon fontSize="small" />
            </Box>
            <Box className={classes.progressWrap}>
              <Box className={classes.progressTop}>
                <Typography className={classes.metricLabel}>Progression</Typography>
                <Typography className={classes.metricValue} sx={{ color: progressColor, fontSize: 20 }}>
                  {documentStats.progress}%
                </Typography>
              </Box>
              <Box className={classes.progressBar}>
                <Box
                  sx={{
                    height: "100%",
                    width: `${documentStats.progress}%`,
                    borderRadius: 999,
                    backgroundColor: progressColor,
                    transition: "width 200ms ease",
                  }}
                />
              </Box>
              <Typography className={classes.metricHelper}>
                {documentStats.validated}/{documentStats.total} valides
              </Typography>
            </Box>
          </Paper>
        </Box>

        {isFullyValidated ? (
          <Box className={classes.successBanner}>
            <Box className={classes.successBannerHeader}>
              <Box className={classes.successIconWrap}>
                <CheckCircleOutlineOutlinedIcon sx={{ color: "#11823b", fontSize: 18 }} />
              </Box>
              <Box>
                <Typography variant="body2" className={classes.successTitle}>
                  Felicitations !
                </Typography>
                <Typography variant="body2" className={classes.successSubtitle}>
                  Tous les documents requis ont ete charges et valides avec succes.
                  Vous pouvez maintenant poursuivre la mise a jour du statut.
                </Typography>
              </Box>
            </Box>

            <Box className={classes.bannerActions}>
              <Button
                variant="outlined"
                color="success"
                disabled={typeof openDocumentsTab !== "function"}
                onClick={handleOpenDocumentsTab}
                startIcon={<DescriptionOutlinedIcon />}
              >
                Voir les documents
              </Button>
              <FormControlLabel
                className={classes.approvalToggle}
                control={
                  <Checkbox
                    color="success"
                    checked={accountStatus === "APPROVED"}
                    onChange={handleApprovalToggle}
                    disabled={!isEditable || accountStatus === "BANNED"}
                  />
                }
                label="On approuve"
              />
              <Button
                variant="contained"
                onClick={handleCreateLewooPayAccount}
                disabled={!canCreateAccount}
                className={classes.bannerActionBtn}
                startIcon={<CheckCircleOutlineOutlinedIcon />}
              >
                {lewooPayCreated ? "Compte créé" : "Créer le compte LewooPay"}
              </Button>
              <Typography variant="caption" className={classes.bannerNote}>
                Consultez les pieces avant toute modification finale.
              </Typography>
            </Box>
          </Box>
        ) : (
          <Box className={classes.warningBanner}>
            <Box className={classes.warningBannerHeader}>
              <Box className={classes.warningIconWrap}>
                <WarningAmberOutlinedIcon sx={{ color: "#b42318", fontSize: 18 }} />
              </Box>
              <Typography variant="body2" className={classes.warningTitle}>
                {documentAttentionCount} documents necessitent une action
              </Typography>
            </Box>
            <Typography variant="body2" className={classes.warningSubtitle}>
              Tous les documents obligatoires doivent etre charges et valides avant de
              pouvoir poursuivre.
            </Typography>

            <Box className={classes.bannerActions}>
              <Button
                variant="contained"
                disabled={typeof openDocumentsTab !== "function"}
                onClick={handleOpenDocumentsTab}
                className={classes.bannerActionBtn}
                startIcon={<DescriptionOutlinedIcon />}
              >
                Completer les documents
              </Button>
              <Typography variant="caption" className={classes.bannerNote}>
                Passez par l'onglet Documents pour charger ou verifier les pieces.
              </Typography>
            </Box>
          </Box>
        )}

        <Box className={classes.tableWrapper}>
          {documentRows.length > 0 ? (
            <DataGrid
              rows={documentRows}
              columns={documentColumns}
              autoHeight
              disableSelectionOnClick
              pageSize={100}
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
              rowHeight={68}
              getRowId={(row) => row?.id}
              className={classes.dataGridTable}
            />
          ) : (
            <Box className={classes.emptyState}>
              <Typography variant="body2">
                Aucun document requis trouve pour ce passenger.
              </Typography>
            </Box>
          )}
        </Box>

        {isFullyValidated && (
          <Box className={classes.infoBanner}>
            <Box className={classes.infoIconWrap}>
              <InfoOutlinedIcon sx={{ color: "#2563eb", fontSize: 18 }} />
            </Box>
            <Box>
              <Typography variant="body2" className={classes.infoTitle}>
                Information
              </Typography>
              <Typography variant="body2" className={classes.infoSubtitle}>
                Tous les documents sont a jour. Vous pouvez continuer avec le statut du compte.
              </Typography>
            </Box>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default Status;
